import { supabase } from '../lib/supabase';

export interface CompetitionLeaderboardItem {
  rank: number;
  id: string;
  publicName: string;
  deliveredOrders: number;
  userId: string;
}

export interface CompetitionParticipant {
  id: string;
  userId: string;
  mastershopEmail: string;
  publicName: string;
  deliveredOrders: number;
  isHidden: boolean;
  optedInAt: string;
  updatedAt: string;
}

export interface CompetitionLeaderboardData {
  updatedAt: string;
  period: {
    startDate: string;
    finalDate: string;
  };
  totalDeliveredGuides: number;
  totalUniquePartnerSellers: number;
  totalEnrolledCompetitors: number;
  ranking: CompetitionLeaderboardItem[];
}

const SETTINGS_KEY_PARTICIPANTS = 'competition_participants';
const SETTINGS_KEY_LEADERBOARD = 'competition_leaderboard_cache';
const SETTINGS_KEY_PARTNER_CONFIG = 'mastershop_partner_api_config';

export const competitionService = {
  /**
   * Obtém os dados públicos do ranking da competição
   */
  async getLeaderboard(): Promise<CompetitionLeaderboardData | null> {
    try {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', SETTINGS_KEY_LEADERBOARD)
        .maybeSingle();

      if (error) {
        console.warn('Erro ao carregar leaderboard da competição:', error);
        return null;
      }

      if (data?.value) {
        return data.value as CompetitionLeaderboardData;
      }
    } catch (err) {
      console.error('Falha ao obter leaderboard:', err);
    }
    return null;
  },

  /**
   * Obtém a lista completa de participantes (apenas para Admin moderar)
   */
  async getAllParticipants(): Promise<CompetitionParticipant[]> {
    try {
      const { data } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', SETTINGS_KEY_PARTICIPANTS)
        .maybeSingle();

      if (data?.value && Array.isArray(data.value)) {
        return data.value as CompetitionParticipant[];
      }
    } catch (err) {
      console.warn('Erro ao obter participantes:', err);
    }
    return [];
  },

  /**
   * Obtém a inscrição do usuário atual, se houver (por userId ou e-mail registrado)
   */
  async getUserParticipation(userId?: string | null, email?: string | null): Promise<CompetitionParticipant | null> {
    const cleanEmail = email ? email.trim().toLowerCase() : null;
    if (!userId && !cleanEmail) return null;
    const participants = await this.getAllParticipants();
    return participants.find((p) => 
      (userId && p.userId === userId) ||
      (cleanEmail && p.mastershopEmail.toLowerCase() === cleanEmail)
    ) || null;
  },

  /**
   * Validação Mágica: verifica em tempo real se o e-mail informado existe na MasterShop
   * e quantas entregas ele já acumula no período oficial.
   */
  async validateMastershopEmail(email: string): Promise<{
    valid: boolean;
    deliveredCount: number;
    businessName?: string;
    error?: string;
  }> {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      return { valid: false, deliveredCount: 0 };
    }

    try {
      // 1. Obter rota e chave da comunidade
      const { data: configRow } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', SETTINGS_KEY_PARTNER_CONFIG)
        .maybeSingle();

      const apiKey = configRow?.value?.api_key || '666b07d4a82437a4f0ba58dc13c51bbc5f08119db471b35f68fc79ad5a52bc7c';
      const serviceRoute = configRow?.value?.service_route || 'api/v1/comunidad-1111/guias-entregadas-c5cd3d1039448269';

      const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const targetUrl = isDev
        ? `/api/mastershop-partner/${serviceRoute}?dateFrom=2026-11-01&dateTo=2026-12-16&dropshipperEmail=${encodeURIComponent(cleanEmail)}`
        : `https://n8n.master.la/webhook/${serviceRoute}?dateFrom=2026-11-01&dateTo=2026-12-16&dropshipperEmail=${encodeURIComponent(cleanEmail)}`;

      const res = await fetch(targetUrl, {
        headers: {
          'x-api-key': apiKey,
          'Accept': 'application/json',
        },
      });

      if (!res.ok) {
        return { valid: false, deliveredCount: 0, error: 'Não foi possível consultar a MasterShop no momento.' };
      }

      const json = await res.json();
      if (json?.data && Array.isArray(json.data) && json.data.length > 0) {
        const total = json.summary?.deliveredGuides || json.data.reduce((acc: number, r: any) => acc + (r.deliveredGuides || 0), 0);
        const business = json.data[0]?.dropshipperBusiness;
        return {
          valid: true,
          deliveredCount: total,
          businessName: business,
        };
      }

      // E-mail válido, porém ainda sem entregas no período
      return {
        valid: true,
        deliveredCount: 0,
        businessName: undefined,
      };
    } catch (err: any) {
      console.warn('Erro ao validar email na MasterShop:', err);
      return { valid: false, deliveredCount: 0, error: err.message };
    }
  },

  /**
   * Inscreve o vendedor no Desafio Black (Etapa 2 do Modal)
   * Suporta tanto usuários logados quanto visitantes por e-mail MasterShop.
   */
  async registerParticipant(params: {
    userId?: string | null;
    mastershopEmail: string;
    publicName: string;
  }): Promise<{ success: boolean; error?: string; participant?: CompetitionParticipant }> {
    const cleanEmail = params.mastershopEmail.trim().toLowerCase();
    const cleanName = params.publicName.trim();
    const userId = params.userId || null;

    if (!cleanEmail || !cleanEmail.includes('@')) {
      return { success: false, error: 'Por favor, insira um e-mail válido da MasterShop.' };
    }

    if (!cleanName || cleanName.length < 2) {
      return { success: false, error: 'Por favor, defina um nome de exibição de pelo menos 2 caracteres.' };
    }

    // 1. Tenta prioritariamente via backend da Netlify (com privilégio de service_role para contornar RLS)
    try {
      const res = await fetch('/api/competition/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'register',
          userId: userId,
          mastershopEmail: cleanEmail,
          publicName: cleanName,
        }),
      });

      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          return { success: true, participant: json.participant };
        } else {
          return { success: false, error: json.error || 'Falha ao processar inscrição no servidor.' };
        }
      }
    } catch (netErr: any) {
      console.warn('Endpoint /api/competition/sync indisponível no cliente, tentando via proxy/admin:', netErr.message);
    }

    // 2. Fallback caso execute em ambiente local Vite proxy (/api/supabase-admin)
    try {
      const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const adminUrl = isDev 
        ? '/api/supabase-admin/rest/v1/platform_settings' 
        : 'https://meqeeisqsfqtplyqqxhv.supabase.co/rest/v1/platform_settings';

      const serviceKey = 'sb_secret_AgXva5TY5iZmZkNiV_yJTg_2ahvx4bm';

      const participants = await this.getAllParticipants();

      let participant = participants.find((p) => 
        p.mastershopEmail.toLowerCase() === cleanEmail ||
        (userId && p.userId === userId)
      );

      if (participant) {
        participant.mastershopEmail = cleanEmail;
        participant.publicName = cleanName;
        if (userId && (!participant.userId || participant.userId.startsWith('guest_'))) {
          participant.userId = userId;
        }
        participant.updatedAt = new Date().toISOString();
        (participant as any).termsAccepted = true;
        (participant as any).termsAcceptedAt = new Date().toISOString();
      } else {
        participant = {
          id: `part_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
          userId: userId || `guest_${cleanEmail.replace(/[^a-z0-9]/gi, '_')}`,
          mastershopEmail: cleanEmail,
          publicName: cleanName,
          deliveredOrders: 0,
          isHidden: false,
          optedInAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        (participant as any).termsAccepted = true;
        (participant as any).termsAcceptedAt = new Date().toISOString();
        participants.push(participant);
      }

      const check = await this.validateMastershopEmail(cleanEmail);
      if (participant && check.valid) {
        participant.deliveredOrders = check.deliveredCount;
      }

      // Salva via proxy admin para ignorar RLS
      const saveRes = await fetch(adminUrl, {
        method: 'POST',
        headers: {
          'apikey': serviceKey,
          'Authorization': `Bearer ${serviceKey}`,
          'Content-Type': 'application/json',
          'Prefer': 'resolution=merge-duplicates',
        },
        body: JSON.stringify({
          key: SETTINGS_KEY_PARTICIPANTS,
          value: participants,
          description: 'Lista de participantes inscritos no Desafio Black (MasterClub)',
          updated_at: new Date().toISOString(),
        }),
      });

      if (!saveRes.ok) {
        throw new Error(`Erro ao salvar inscrição: HTTP ${saveRes.status}`);
      }

      await this.recalculateLeaderboard(participants);

      return { success: true, participant };
    } catch (err: any) {
      console.error('Erro ao inscrever competidor:', err);
      return { success: false, error: err.message || 'Erro ao salvar inscrição no banco.' };
    }
  },

  /**
   * Recurso de Moderação Administrativa: Alterna a visibilidade de um membro no ranking
   */
  async toggleParticipantVisibility(
    participantId: string,
    isHidden: boolean
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // 1. Tenta via Netlify backend
      const res = await fetch('/api/competition/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'toggle-visibility',
          participantId,
          isHidden,
        }),
      });

      if (res.ok) {
        const json = await res.json();
        if (json.success) return { success: true };
      }
    } catch (e: any) {
      console.warn('Fallback para moderação local:', e.message);
    }

    try {
      const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const adminUrl = isDev 
        ? '/api/supabase-admin/rest/v1/platform_settings' 
        : 'https://meqeeisqsfqtplyqqxhv.supabase.co/rest/v1/platform_settings';

      const serviceKey = 'sb_secret_AgXva5TY5iZmZkNiV_yJTg_2ahvx4bm';

      const participants = await this.getAllParticipants();
      const target = participants.find((p) => p.id === participantId);

      if (!target) {
        return { success: false, error: 'Participante não encontrado.' };
      }

      target.isHidden = isHidden;
      target.updatedAt = new Date().toISOString();

      await fetch(adminUrl, {
        method: 'POST',
        headers: {
          'apikey': serviceKey,
          'Authorization': `Bearer ${serviceKey}`,
          'Content-Type': 'application/json',
          'Prefer': 'resolution=merge-duplicates',
        },
        body: JSON.stringify({
          key: SETTINGS_KEY_PARTICIPANTS,
          value: participants,
          description: 'Lista de participantes inscritos no Desafio Black (MasterClub)',
          updated_at: new Date().toISOString(),
        }),
      });

      await this.recalculateLeaderboard(participants);

      return { success: true };
    } catch (err: any) {
      console.error('Erro ao alternar visibilidade de participante:', err);
      return { success: false, error: err.message || 'Falha ao atualizar no banco.' };
    }
  },

  /**
   * Moderação Admin: Atualiza o nome público de um participante no ranking
   */
  async updateParticipantName(participantId: string, newPublicName: string): Promise<{ success: boolean; error?: string }> {
    const cleanName = newPublicName.trim();
    if (!cleanName || cleanName.length < 2) {
      return { success: false, error: 'O nome deve conter pelo menos 2 caracteres.' };
    }

    // 1. Tenta via Netlify Functions (service_role)
    try {
      const res = await fetch('/api/competition/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update-name',
          participantId,
          newPublicName: cleanName,
        }),
      });

      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          return { success: true };
        } else {
          return { success: false, error: json.error || 'Falha ao atualizar nome no servidor.' };
        }
      }
    } catch (netErr: any) {
      console.warn('Endpoint /api/competition/sync indisponível, usando fallback:', netErr.message);
    }

    // 2. Fallback caso execute localmente com Vite proxy
    try {
      const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
      const adminUrl = isDev 
        ? '/api/supabase-admin/rest/v1/platform_settings' 
        : 'https://meqeeisqsfqtplyqqxhv.supabase.co/rest/v1/platform_settings';

      const serviceKey = 'sb_secret_AgXva5TY5iZmZkNiV_yJTg_2ahvx4bm';

      const participants = await this.getAllParticipants();
      const target = participants.find((p) => p.id === participantId);

      if (!target) {
        return { success: false, error: 'Participante não encontrado.' };
      }

      target.publicName = cleanName;
      target.updatedAt = new Date().toISOString();

      await fetch(adminUrl, {
        method: 'POST',
        headers: {
          'apikey': serviceKey,
          'Authorization': `Bearer ${serviceKey}`,
          'Content-Type': 'application/json',
          'Prefer': 'resolution=merge-duplicates',
        },
        body: JSON.stringify({
          key: SETTINGS_KEY_PARTICIPANTS,
          value: participants,
          description: 'Lista de participantes inscritos no Desafio Black (MasterClub)',
          updated_at: new Date().toISOString(),
        }),
      });

      await this.recalculateLeaderboard(participants);

      return { success: true };
    } catch (err: any) {
      console.error('Erro ao atualizar nome de participante:', err);
      return { success: false, error: err.message || 'Falha ao atualizar no banco.' };
    }
  },

  /**
   * Recalcula as posições do ranking ordenadas por entregas decrescentes
   */
  async recalculateLeaderboard(participants: CompetitionParticipant[]): Promise<void> {
    const active = participants.filter((p) => !p.isHidden);
    active.sort((a, b) => b.deliveredOrders - a.deliveredOrders);

    const ranking: CompetitionLeaderboardItem[] = active.map((p, idx) => ({
      rank: idx + 1,
      id: p.id,
      publicName: p.publicName,
      deliveredOrders: p.deliveredOrders,
      userId: p.userId,
    }));

    const totalDelivered = active.reduce((acc, p) => acc + p.deliveredOrders, 0);

    const leaderboardCache: CompetitionLeaderboardData = {
      updatedAt: new Date().toISOString(),
      period: {
        startDate: '2026-11-01',
        finalDate: '2026-12-16',
      },
      totalDeliveredGuides: totalDelivered,
      totalUniquePartnerSellers: participants.length,
      totalEnrolledCompetitors: active.length,
      ranking,
    };

    const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    const adminUrl = isDev 
      ? '/api/supabase-admin/rest/v1/platform_settings' 
      : 'https://meqeeisqsfqtplyqqxhv.supabase.co/rest/v1/platform_settings';

    const serviceKey = 'sb_secret_AgXva5TY5iZmZkNiV_yJTg_2ahvx4bm';

    try {
      await fetch(adminUrl, {
        method: 'POST',
        headers: {
          'apikey': serviceKey,
          'Authorization': `Bearer ${serviceKey}`,
          'Content-Type': 'application/json',
          'Prefer': 'resolution=merge-duplicates',
        },
        body: JSON.stringify({
          key: SETTINGS_KEY_LEADERBOARD,
          value: leaderboardCache,
          description: 'Cache público do Ranking Black Challenge atualizado pelo cron a cada 1 hora',
          updated_at: new Date().toISOString(),
        }),
      });
    } catch (e) {
      // Fallback padrão Supabase client
      await supabase
        .from('platform_settings')
        .upsert({
          key: SETTINGS_KEY_LEADERBOARD,
          value: leaderboardCache,
          description: 'Cache público do Ranking Black Challenge atualizado pelo cron a cada 1 hora',
          updated_at: new Date().toISOString(),
        });
    }
  },
};

